import { BookingLink } from "../components/BookingLink.tsx";
import { Reveal } from "../components/Reveal.tsx";
import { Accented, plainText } from "../components/Accented.tsx";
import { STUDIO } from "../lib/studio.ts";
import type { SiteContent } from "../lib/api.ts";

/**
 * Everything after the introduction is optional in admin, and a blank line is
 * left off the page. The defaults below only show before /api/studio answers,
 * so the optional ones default to blank too, rather than flashing a quote that
 * then vanishes.
 */
const FALLBACK = {
  "about.eyebrow": "Meet your therapist",
  "about.name": "Louise *Legge*",
  "about.lede": "Hi, I'm Lou, the face and hands behind The Renewal Studio. After many years in the corporate world, I decided it was time for a change. As a mum of two, I know how easy it is to put yourself last, and I wanted to create something meaningful — a space where I could help people look and feel their best, while giving them the opportunity to truly switch off.",
  "about.story": "Having spent much of my life navigating the pressures of a busy mind and everyday stress, I know how difficult it can be to truly unwind. That's why I wanted to create a space where people can slow down, feel at ease and enjoy a moment of calm away from the demands of everyday life. My approach combines results-driven skincare with restorative massage and sculpting techniques, offering treatments that nurture your skin while encouraging your mind and body to relax.\n\nFrom my cosy, at-home private studio in Wokingham, my aim is simple: to help you feel comfortable, cared for and leave feeling refreshed, confident and renewed. I look forward to welcoming you. Lou x",
  "about.quote": "",
  "about.qualifications": "",
  "about.skincare": "",
  "about.experience": "",
  "about.specialisms": "Bespoke facials, gua sha, facial massage",
};

const CREDENTIALS = [
  { key: "about.qualifications", label: "Qualifications" },
  { key: "about.skincare", label: "Skincare" },
  { key: "about.experience", label: "Experience" },
  { key: "about.specialisms", label: "Specialisms" },
] as const;

export function About({ content }: { content: SiteContent }) {
  const text = (key: keyof typeof FALLBACK) =>
    (content[key] ?? FALLBACK[key]).trim();

  const name = text("about.name");
  const firstName = plainText(name).split(/\s+/)[0] ?? "";
  // The quotation marks are added here, so any the studio typed are dropped
  // rather than doubled.
  const quote = text("about.quote").replace(/^["'“”‘’]+|["'“”‘’]+$/g, "").trim();
  const credentials = CREDENTIALS.map((item) => ({
    ...item,
    value: text(item.key),
  })).filter((item) => item.value.length > 0);

  return (
    <section className="about" id="about">
      <div className="page about__inner">
        <Reveal className="about__portrait-wrap">
          {/*
            Drop a portrait at web/public/images/therapist.jpg and it takes over
            from the panel with no code change. The crest overlaps the corner
            either way.
          */}
          <figure className="about__portrait placeholder">
            <img
              src="/images/therapist.jpg"
              alt={plainText(name)}
              loading="lazy"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
            <svg className="about__mark" viewBox="0 0 120 120" aria-hidden="true">
              <circle cx="60" cy="60" r="46" />
              <circle cx="60" cy="60" r="33" />
              <path d="M60 14 C 42 40, 42 80, 60 106 C 78 80, 78 40, 60 14 Z" />
            </svg>
          </figure>
          <img
            className="about__badge"
            src="/images/logo.webp"
            alt=""
            width={104}
            height={103}
            loading="lazy"
          />
        </Reveal>

        <Reveal delay={120} className="about__copy">
          <p className="eyebrow">{text("about.eyebrow")}</p>
          <h2 className="about__name">
            <Accented text={name} />
          </h2>
          {text("about.lede").split(/\n\s*\n/).map((paragraph, index) => (
            <p className="about__lede" key={`lede-${index}`}>{paragraph}</p>
          ))}
          {text("about.story") && (
            <div className="about__story">
              {text("about.story").split(/\n\s*\n/).map((paragraph, index) => <p key={index}>{paragraph}</p>)}
            </div>
          )}
          {quote && (
            <blockquote className="about__quote">
              <p>&ldquo;{quote}&rdquo;</p>
            </blockquote>
          )}

          {credentials.length > 0 && (
            <dl className="about__credentials">
              {credentials.map((item) => (
                <div key={item.key}>
                  <dt>{item.label}</dt>
                  <dd>{item.value}</dd>
                </div>
              ))}
            </dl>
          )}

          <div className="about__actions">
            <BookingLink className="btn btn--lg">
              {firstName ? `Book with ${firstName}` : "Book an appointment"}
            </BookingLink>
            <a
              className="text-link"
              href={`https://instagram.com/${STUDIO.instagram}`}
              target="_blank"
              rel="noreferrer noopener"
            >
              Follow on Instagram
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
